import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { ArrowLeft, ShoppingCart, Box } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [quantityInput, setQuantityInput] = useState("1"); // Separate state for input display

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}`);
      setProduct(response.data);
      const moq = response.data.moq || 1;
      setSelectedQuantity(moq);
      setQuantityInput(String(moq));
    } catch (error) {
      console.error("Error fetching product:", error);
      toast.error("Failed to load product details");
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPrice = () => {
    if (!product || !product.price_tiers || product.price_tiers.length === 0) {
      return product?.base_price || 0;
    }

    // Sort tiers by min_quantity descending and find the highest applicable tier
    const sortedTiers = [...product.price_tiers].sort((a, b) => b.min_quantity - a.min_quantity);
    const activeTier = sortedTiers.find(tier => selectedQuantity >= tier.min_quantity);
    
    return activeTier ? activeTier.price_per_unit : product.base_price;
  };

  const getPackagingCost = () => {
    return product?.packaging_cost || 0;
  };

  const handleAddToQuote = () => {
    const quoteItems = JSON.parse(localStorage.getItem("quoteItems") || "[]");
    
    const existingIndex = quoteItems.findIndex(item => item.product_id === productId);
    const currentPrice = getCurrentPrice();
    const packagingCost = getPackagingCost();
    
    if (existingIndex >= 0) {
      quoteItems[existingIndex].quantity = selectedQuantity;
      quoteItems[existingIndex].price_per_unit = currentPrice;
      quoteItems[existingIndex].packaging_cost = packagingCost;
      toast.success("Quote updated");
    } else {
      quoteItems.push({
        product_id: productId,
        product_name: product.name,
        quantity: selectedQuantity,
        price_per_unit: currentPrice,
        packaging_cost: packagingCost,
        notes: ""
      });
      toast.success("Added to quote request");
    }

    localStorage.setItem("quoteItems", JSON.stringify(quoteItems));
  };

  const handleGoToQuote = () => {
    handleAddToQuote();
    navigate("/quote");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-xl">Loading product...</div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2">Product not found</h2>
            <Link to="/catalog">
              <Button>Back to Catalog</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const currentPrice = getCurrentPrice();
  const packagingCost = getPackagingCost();
  const totalPerUnit = currentPrice + packagingCost;
  const totalCost = totalPerUnit * selectedQuantity;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1">
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <Link to="/catalog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-8" data-testid="back-to-catalog-link">
              <ArrowLeft className="h-4 w-4" />
              Back to Catalog
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              {/* Images and Video */}
              <div className="lg:col-span-6">
                {product.images && product.images.length > 0 ? (
                  product.images.length === 1 ? (
                    <div className="border border-border/50 rounded-sm overflow-hidden">
                      <img
                        src={product.images[0]}
                        alt={product.name}
                        className="w-full h-auto"
                        data-testid="product-main-image"
                      />
                    </div>
                  ) : (
                    <Carousel className="w-full">
                      <CarouselContent>
                        {product.images.map((image, index) => (
                          <CarouselItem key={index}>
                            <div className="border border-border/50 rounded-sm overflow-hidden">
                              <img
                                src={image}
                                alt={`${product.name} - ${index + 1}`}
                                className="w-full h-auto"
                              />
                            </div>
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                      <CarouselPrevious />
                      <CarouselNext />
                    </Carousel>
                  )
                ) : (
                  <div className="border border-border/50 rounded-sm h-96 flex items-center justify-center bg-muted/30">
                    <Box className="h-24 w-24 text-muted-foreground" />
                  </div>
                )}
                
                {/* Product Video */}
                {product.video_url && (
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Manrope' }}>Product Demo Video</h3>
                    <div className="border border-border/50 rounded-sm overflow-hidden">
                      {product.video_url.includes('youtube.com') || product.video_url.includes('youtu.be') ? (
                        <iframe
                          src={product.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                          className="w-full aspect-video"
                          allowFullScreen
                          title="Product Video"
                        />
                      ) : product.video_url.includes('vimeo.com') ? (
                        <iframe
                          src={product.video_url.replace('vimeo.com/', 'player.vimeo.com/video/')}
                          className="w-full aspect-video"
                          allowFullScreen
                          title="Product Video"
                        />
                      ) : (
                        <video
                          src={product.video_url}
                          controls
                          className="w-full"
                        >
                          Your browser does not support the video tag.
                        </video>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="lg:col-span-6">
                <div className="mb-4">
                  <span className="text-xs font-bold tracking-widest uppercase text-muted-foreground" data-testid="product-code">
                    {product.code}
                  </span>
                </div>

                <h1 className="text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope' }} data-testid="product-name">
                  {product.name}
                </h1>

                {product.highlights && product.highlights.length > 0 && (
                  <ul className="space-y-2 mb-6">
                    {product.highlights.map((highlight, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span className="text-primary mt-1">✔</span>
                        <span className="text-muted-foreground">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="border-t border-b border-border/50 py-6 my-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Material</div>
                      <div className="font-semibold text-base">{product.material}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Dimensions</div>
                      <div className="font-semibold text-base">{product.dimensions}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Minimum Order Quantity</div>
                      <div className="font-semibold text-base" data-testid="product-moq">
                        {product.moq} units
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Unit Weight</div>
                      <div className="font-semibold text-base" data-testid="product-unit-weight">
                        {product.unit_weight || "N/A"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Pricing */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Manrope' }}>Pricing Structure</h3>
                  
                  {/* Sample Cost Box */}
                  {product.sample_cost && (
                    <div className="mb-4 p-3 border border-border/50 rounded-sm bg-amber-50/50">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Sample Cost</span>
                        <span className="text-sm font-bold text-amber-700" style={{ fontFamily: 'Manrope' }}>
                          ₹{product.sample_cost.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 mb-6">
                    {product.price_tiers && product.price_tiers.length > 0 ? (
                      product.price_tiers.map((tier, idx) => {
                        // Sort tiers by min_quantity descending to find the highest applicable tier
                        const sortedTiers = [...product.price_tiers].sort((a, b) => b.min_quantity - a.min_quantity);
                        const activeTier = sortedTiers.find(t => selectedQuantity >= t.min_quantity);
                        const isActive = activeTier && tier.min_quantity === activeTier.min_quantity;
                        
                        return (
                          <div
                            key={idx}
                            className={`p-4 border rounded-sm transition-all ${
                              isActive 
                                ? 'price-tier-active border-primary' 
                                : 'border-border/50 bg-white'
                            }`}
                            data-testid={`price-tier-${idx}`}
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <span className={isActive ? 'font-semibold' : 'text-muted-foreground'}>
                                  {tier.min_quantity}+ units
                                </span>
                                {tier.discount_percent > 0 && (
                                  <div className={`text-sm ${isActive ? 'text-green-600 font-medium' : 'text-green-600/70'}`}>
                                    ({tier.discount_percent}% off)
                                  </div>
                                )}
                              </div>
                              <span className={`text-xl font-bold ${
                                isActive ? '' : 'text-muted-foreground'
                              }`} style={{ fontFamily: 'Manrope' }}>
                                ₹{tier.price_per_unit.toLocaleString()}/unit
                              </span>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-4 border border-border/50 rounded-sm bg-white">
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">Base Price</span>
                          <span className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
                            ₹{product.base_price.toLocaleString()}/unit
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="bg-muted/30 p-4 rounded-sm border border-border/50">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-muted-foreground">Your Quantity:</span>
                      <input
                        type="number"
                        min={product.moq}
                        value={quantityInput}
                        onChange={(e) => {
                          // Allow free typing - store raw input value
                          setQuantityInput(e.target.value);
                          // Update actual quantity if valid number
                          const parsed = parseInt(e.target.value);
                          if (!isNaN(parsed) && parsed > 0) {
                            setSelectedQuantity(parsed);
                          }
                        }}
                        onBlur={() => {
                          // On blur, validate and enforce MOQ
                          const parsed = parseInt(quantityInput);
                          if (isNaN(parsed) || parsed < product.moq) {
                            setQuantityInput(String(product.moq));
                            setSelectedQuantity(product.moq);
                          } else {
                            setQuantityInput(String(parsed));
                            setSelectedQuantity(parsed);
                          }
                        }}
                        className="w-24 px-3 py-2 border border-input rounded-sm text-right"
                        data-testid="quantity-input"
                      />
                    </div>
                    <div className="flex justify-between items-center py-2 border-t border-border/50">
                      <span className="text-sm font-semibold">Price per Unit:</span>
                      <span className="text-xl font-bold text-primary" style={{ fontFamily: 'Manrope' }} data-testid="current-price">
                        ₹{currentPrice.toLocaleString()}
                      </span>
                    </div>
                    {packagingCost > 0 && (
                      <div className="flex justify-between items-center py-2 border-t border-border/50">
                        <span className="text-sm font-semibold">Packaging per Unit:</span>
                        <span className="text-xl font-bold text-primary" style={{ fontFamily: 'Manrope' }} data-testid="packaging-cost">
                          ₹{packagingCost.toLocaleString()}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-3 border-t-2 border-primary/30 mt-2">
                      <span className="text-sm font-bold">Total (excl. GST):</span>
                      <span className="text-2xl font-bold text-primary" style={{ fontFamily: 'Manrope' }} data-testid="total-cost">
                        ₹{totalCost.toLocaleString()}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground text-right mt-1">
                      {selectedQuantity} × ₹{totalPerUnit.toLocaleString()} per unit
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-4">
                  <Button onClick={handleAddToQuote} variant="outline" className="flex-1" data-testid="add-to-quote-btn">
                    Add to Quote
                  </Button>
                  <Button onClick={handleGoToQuote} className="flex-1 gap-2" data-testid="request-quote-btn">
                    <ShoppingCart className="h-5 w-5" />
                    Request Quote
                  </Button>
                </div>

                {/* Full Specifications */}
                {product.full_specs && Object.keys(product.full_specs).length > 0 && (
                  <div className="mt-8 p-6 border border-border/50 rounded-sm bg-muted/20">
                    <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Manrope' }}>Full Specifications</h3>
                    <dl className="space-y-3">
                      {Object.entries(product.full_specs).map(([key, value]) => (
                        <div key={key} className="flex justify-between border-b border-border/30 pb-2">
                          <dt className="text-muted-foreground capitalize">{key.replace(/_/g, ' ')}:</dt>
                          <dd className="font-medium text-right">{value}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}